import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  BadgeDollarSign,
  Check,
  ChevronDown,
  Clock3,
  Loader2,
  PackageCheck,
  RefreshCw,
  ShieldAlert,
  TrendingDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useCurrency } from '@/hooks/useCurrency';
import { useLocationStore } from '@/store/location';
import { MorningBriefSignal, MorningBriefSignalType, useMorningBrief } from '@/hooks/useMorningBrief';
import { cn } from '@/lib/utils';
import { DashboardLayout } from '@/components/layout/DashboardLayout';

const SIGNAL_CONFIG: Record<MorningBriefSignalType, { label: string; icon: typeof PackageCheck; tone: string }> = {
  inventory: { label: 'Inventory', icon: PackageCheck, tone: 'text-emerald-600 bg-emerald-500/10' },
  void_discount: { label: 'Sales controls', icon: ShieldAlert, tone: 'text-amber-600 bg-amber-500/10' },
  menu_margin: { label: 'Menu margin', icon: BadgeDollarSign, tone: 'text-violet-600 bg-violet-500/10' },
  slow_hour: { label: 'Slow hour', icon: Clock3, tone: 'text-sky-600 bg-sky-500/10' },
};

type BriefFilter = 'all' | MorningBriefSignalType | 'completed';

export default function MorningBrief() {
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();
  const { currentLocation, locationScope } = useLocationStore();
  const { signals, isLoading, reviewAmount, updateAction } = useMorningBrief();
  const [filter, setFilter] = useState<BriefFilter>('all');
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const visibleSignals = useMemo(() => signals.filter((signal) => {
    if (filter === 'completed') return signal.status === 'actioned' || signal.status === 'dismissed';
    return filter === 'all' || signal.type === filter;
  }), [filter, signals]);

  const needsAction = signals.filter((signal) => !signal.status || signal.status === 'open' || signal.status === 'reviewed').length;
  const actedCount = signals.filter((signal) => signal.status === 'actioned').length;
  const scopeLabel = locationScope === 'single' && currentLocation ? currentLocation.name : 'All accessible locations';

  const recordAndNavigate = async (signal: MorningBriefSignal) => {
    try {
      await updateAction.mutateAsync({ signal, status: 'reviewed' });
      navigate(signal.actionHref);
    } catch {
      navigate(signal.actionHref);
    }
  };

  const header = <div className="flex items-center gap-3"><div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10"><TrendingDown className="h-5 w-5 text-primary" /></div><div><h1 className="text-xl font-display font-semibold tracking-tight">Morning Brief</h1><p className="text-sm text-muted-foreground">Prioritized operating actions for {scopeLabel}</p></div></div>;

  return (
    <DashboardLayout header={header}>
      <div className="mx-auto max-w-6xl space-y-8 pb-12 antialiased">
      <header className="flex flex-col gap-5 border-b border-border pb-7 lg:flex-row lg:items-end lg:justify-between">
        <div className="max-w-2xl">
          <div className="flex items-center gap-2 text-sm font-medium text-primary"><span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10"><TrendingDown className="h-4 w-4" /></span> Morning Brief</div>
          <h1 className="mt-3 text-3xl font-display font-semibold tracking-tight text-wrap-balance">Today’s operational actions</h1>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">A prioritized 30-day review for {scopeLabel}. Values are evidence-backed review amounts, not claimed savings.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right text-xs text-muted-foreground"><div>Prepared now</div><div className="mt-0.5 font-medium text-foreground">Manual approval required</div></div>
          <Button variant="outline" className="active:scale-[0.96]" onClick={() => window.location.reload()}><RefreshCw className="mr-2 h-4 w-4" />Refresh</Button>
        </div>
      </header>

      <section className="grid gap-6 border-b border-border pb-7 lg:grid-cols-[1.25fr_1fr]">
        <div className="flex flex-col justify-between gap-5">
          <div>
            <p className="text-sm font-medium text-muted-foreground">Needs action today</p>
            <div className="mt-2 flex items-baseline gap-3"><span className="text-5xl font-semibold tracking-tight tabular-nums">{needsAction}</span><span className="text-sm text-muted-foreground">prioritized signal{needsAction === 1 ? '' : 's'}</span></div>
          </div>
          <div className="flex flex-wrap gap-2">
            <BriefFilterButton active={filter === 'all'} onClick={() => setFilter('all')}>All actions</BriefFilterButton>
            <BriefFilterButton active={filter === 'inventory'} onClick={() => setFilter('inventory')}>Restock risks</BriefFilterButton>
            <BriefFilterButton active={filter === 'void_discount'} onClick={() => setFilter('void_discount')}>Review voids</BriefFilterButton>
            <BriefFilterButton active={filter === 'menu_margin'} onClick={() => setFilter('menu_margin')}>Protect margin</BriefFilterButton>
            <BriefFilterButton active={filter === 'slow_hour'} onClick={() => setFilter('slow_hour')}>Fill slow hours</BriefFilterButton>
          </div>
        </div>
        <div className="border-l-0 border-border pt-1 lg:border-l lg:pl-7">
          <p className="text-sm font-medium text-muted-foreground">Sales-control review amount</p>
          <div className="mt-2 text-3xl font-semibold tracking-tight tabular-nums">{formatCurrency(reviewAmount)}</div>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">Voids and discounts from the last seven days. Confirm the cause before treating this as recoverable value.</p>
          {actedCount > 0 && <p className="mt-3 text-xs font-medium text-emerald-600">{actedCount} action{actedCount === 1 ? '' : 's'} marked complete</p>}
        </div>
      </section>

      <section aria-label="Morning Brief actions">
        <div className="mb-4 flex items-center justify-between"><h2 className="text-base font-semibold">Action queue</h2><span className="text-xs text-muted-foreground">Each action opens an existing, permissioned BlazePOS workflow.</span></div>
        {isLoading ? <div className="flex min-h-64 items-center justify-center text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Reviewing operational signals…</div> : (
          <div className="divide-y divide-border rounded-2xl bg-card shadow-[0_1px_2px_rgba(15,23,42,0.06),0_10px_26px_rgba(15,23,42,0.04)] ring-1 ring-black/5 dark:ring-white/10">
            <AnimatePresence initial={false}>
              {visibleSignals.map((signal, index) => <SignalRow key={signal.key} signal={signal} index={index} formatCurrency={formatCurrency} expanded={expandedKey === signal.key} onToggleEvidence={() => setExpandedKey((key) => key === signal.key ? null : signal.key)} onOpen={() => void recordAndNavigate(signal)} onComplete={() => updateAction.mutate({ signal, status: 'actioned' })} pending={updateAction.isPending} />)}
            </AnimatePresence>
            {visibleSignals.length === 0 && <div className="px-6 py-16 text-center text-sm text-muted-foreground">No actions match this view.</div>}
          </div>
        )}
      </section>
      </div>
    </DashboardLayout>
  );
}

function BriefFilterButton({ active, children, onClick }: { active: boolean; children: React.ReactNode; onClick: () => void }) {
  return <Button variant={active ? 'default' : 'outline'} size="sm" className="rounded-lg active:scale-[0.96]" onClick={onClick}>{children}</Button>;
}

function SignalRow({ signal, index, formatCurrency, expanded, onToggleEvidence, onOpen, onComplete, pending }: { signal: MorningBriefSignal; index: number; formatCurrency: (value: number) => string; expanded: boolean; onToggleEvidence: () => void; onOpen: () => void; onComplete: () => void; pending: boolean }) {
  const config = SIGNAL_CONFIG[signal.type];
  const Icon = config.icon;
  const completed = signal.status === 'actioned' || signal.status === 'dismissed';
  return <motion.article initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.22, delay: Math.min(index * 0.06, 0.24) }} className={cn('px-5 py-5 sm:px-6', completed && 'opacity-60')}>
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
      <div className="flex min-w-0 gap-4">
        <div className={cn('mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl', config.tone)}><Icon className="h-5 w-5" /></div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2"><Badge variant="outline" className="font-medium">{config.label}</Badge>{signal.priority === 'high' && <Badge className="bg-amber-500 text-white hover:bg-amber-500">High priority</Badge>}{completed && <Badge variant="secondary">Complete</Badge>}</div>
          <h3 className="mt-2 font-semibold text-wrap-balance">{signal.title}</h3>
          <p className="mt-1 max-w-2xl text-sm leading-6 text-muted-foreground">{signal.summary}</p>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs"><span className="font-medium tabular-nums text-foreground">{signal.value ? formatCurrency(signal.value) : '—'}</span><span className="text-muted-foreground">{signal.valueLabel}</span></div>
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2 lg:justify-end"><Button size="sm" variant="ghost" className="active:scale-[0.96]" onClick={onToggleEvidence}>Evidence <ChevronDown className={cn('ml-1 h-4 w-4 transition-transform', expanded && 'rotate-180')} /></Button>{!completed && <><Button size="sm" variant="outline" className="active:scale-[0.96]" onClick={onComplete} disabled={pending}><Check className="mr-1.5 h-4 w-4" />Complete</Button><Button size="sm" className="active:scale-[0.96]" onClick={onOpen}>{signal.actionLabel}<ArrowRight className="ml-1.5 h-4 w-4" /></Button></>}</div>
    </div>
    <AnimatePresence initial={false}>{expanded && <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.18 }} className="overflow-hidden"><pre className="mt-4 max-h-48 overflow-auto rounded-xl bg-muted/60 p-3 text-xs leading-5 text-muted-foreground">{JSON.stringify(signal.evidence, null, 2)}</pre></motion.div>}</AnimatePresence>
  </motion.article>;
}
